/**
 * useUnits — ViewModel for /manage/units. Full list + add + delete
 * (no edit — units are immutable).
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { UnitDto } from '../models/unit.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'

export type { UnitDto } from '../models/unit.model'

export interface UseUnitsReturn {
  readonly items: UnitDto[]
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
  readonly add: (name: string) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: { readonly items: UnitDto[] }
}

export function useUnits(): UseUnitsReturn {
  const { authedFetch } = useAuth()
  const [items, setItems] = useState<UnitDto[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState<number>(0)

  useEffect(() => {
    let cancelled = false
    const run = async (): Promise<void> => {
      setIsLoading(true)
      setError(null)
      const res = await authedFetch(APIROUTES.MANAGEUNITS)
      if (cancelled) return
      if (!res.ok) {
        const { message } = await parseApiError(res)
        if (!cancelled) {
          setError(message)
          setIsLoading(false)
        }
        return
      }
      const body = (await res.json()) as ListBody
      if (cancelled) return
      setItems(body.data.items)
      setIsLoading(false)
    }
    void run()
    return () => { cancelled = true }
  }, [authedFetch, tick])

  const reload = useCallback((): void => setTick((t) => t + 1), [])

  const add = useCallback(
    async (name: string) => {
      const r = await mutateJson<UnitDto>(authedFetch, APIROUTES.MANAGEUNITS, 'POST', { name })
      if (!r.ok) return r
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGEUNITITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      reload()
      return { ok: true as const }
    },
    [authedFetch, reload]
  )

  return { items, isLoading, error, reload, add, remove }
}
